// angular
import {
  afterNextRender,
  Component,
  effect,
  inject,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
// components
import { HeroSectionComponent } from '@portfolio/components/hero-section/hero-section.component';
import { AboutSectionComponent } from '@portfolio/components/about-section/about-section.component';
import { CompanyVisionSectionComponent } from '@portfolio/components/company-vision-section/company-vision-section.component';
import { TeamMembersSectionComponent } from '@portfolio/components/team-members-section/team-members-section.component';
import { PortfolioHeaderComponent } from './components/portfolio-header/portfolio-header.component';
import { PortfolioFooterComponent } from './components/portfolio-footer/portfolio-footer.component';
import { ChatBoxComponent } from '@app/+chat/chat-box.component';
import { PortfolioSkeletonComponent } from './components/portfolio-skeleton/portfolio-skeleton.component';
import { TopProductsSectionComponent } from '@app/+home/components/top-products-section/top-products-section.component';
import { ContactSectionComponent } from '@app/+about/components/contact-section/contact-section.component';
// services
import { PortfolioService } from './shared/services/portfolio.service';
import { ProductsService } from '@app/+products/shared/services/products.service';
// interfaces & enums
import { ContactInfo } from '@app/+about/shared/types/contact-info.type';
import { IPortfolioFooterData } from './shared/interfaces/portfolio-footer-data.interface';
import { IPortfolioHeaderData } from './shared/interfaces/portfolio-header-data.interface';
import { IPortfolioHeroData } from './shared/interfaces/portfolio-hero-data.interface';
import { IPortfolioCategoriesData } from './shared/interfaces/portfolio-categories.interface';
import { IPortfolioAboutData } from './shared/interfaces/portfolio-about-data.interface';
import { IPortfolioVideoData } from './shared/interfaces/portfolio-video.interface';
import { IProduct } from '@app/+products/shared/interfaces/product.interface';
import { IPortfolioMembersData } from './shared/interfaces/portfolio-members.interface';
import { II18n } from '@app/shared/interfaces/i18n.interface';
import { IBusinessContactInfo } from '@app/+suppliers/shared/interfaces/business.interface';
import { I18n } from '@app/shared/enums/i18n.enum';

@Component({
  selector: 'pwj-portfolio',
  standalone: true,
  imports: [
    PortfolioHeaderComponent,
    PortfolioFooterComponent,
    HeroSectionComponent,
    AboutSectionComponent,
    ContactSectionComponent,
    CompanyVisionSectionComponent,
    TopProductsSectionComponent,
    TeamMembersSectionComponent,
    ChatBoxComponent,
    PortfolioSkeletonComponent,
  ],
  templateUrl: './portfolio.component.html',
  styleUrl: './portfolio.component.scss',
})
export class PortfolioComponent implements OnInit {
  public showCooperationBanner: boolean = false;
  public companyTitle!: II18n;
  public businessId!: string;
  public headerData!: IPortfolioHeaderData;
  public heroData!: IPortfolioHeroData;
  public categories!: IPortfolioCategoriesData;
  public aboutData!: IPortfolioAboutData;
  public topProducts!: IProduct[];
  public businessProductsCount!: number;
  public isBusinessProductsLoading!: boolean;
  public companyVideo!: IPortfolioVideoData;
  public membersData!: IPortfolioMembersData;
  public footerData!: IPortfolioFooterData;
  public contactInfo!: ContactInfo[];
  public currentLang!: I18n;
  public isLoading!: boolean;

  private _businessId!: string;

  // dependencies
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _portfolioService = inject(PortfolioService);
  private readonly _productsService = inject(ProductsService);
  private readonly _titleService = inject(Title);
  private readonly _translateService = inject(TranslateService);

  constructor() {
    // set business id
    this._activatedRoute.params.subscribe(params => {
      this._businessId = params?.['id'];
    });

    // set loading state

    effect(() => {
      this.isLoading = this._portfolioService.isPortfolioLoading();
    });

    // set current lang
    this.currentLang = (I18n as any)[this._translateService.currentLang];
    this._translateService.onLangChange.subscribe(({ lang }) => {
      this.currentLang = (I18n as any)[lang];
    });

    // get portfolio data from service
    effect(() => {
      this.getPortfolioData();
    });

    // get business products
    effect(() => {
      this.topProducts = this._productsService.businessTopProductsList();
    });

    effect(() => {
      this.businessProductsCount =
        this._productsService.businessTopProductsCount();
    });

    effect(() => {
      this.isBusinessProductsLoading =
        this._productsService.isBusinessProductsLoading();
    });

    effect(() => {
      const businessInfoData = this._portfolioService.portfolioContactInfo();
      this.setBusinessContactInfo(businessInfoData);
    });
  }

  ngOnInit(): void {
    // fetch portfolio data
    this._portfolioService.getPortfolioData(this._businessId);
  }

  public getPortfolioData() {
    const data = this._portfolioService?.portfolioData();

    if (data._id) this.businessId = data._id;

    // set categories data

    this.topProducts = this._productsService.businessTopProductsList();

    // set header data
    this.headerData = {
      title: data?.title,
      code: data?.code,
      logo: data?.logo,
      mobile:
        data?.mobile?.mobile_number &&
        !data?.mobile?.mobile_number?.includes('000000')
          ? data?.mobile?.prefix + data?.mobile?.mobile_number
          : '-',
      website_url: data?.website_url,
    };
    // set hero data
    this.heroData = {
      ...data,
      title: data?.title,
      mobile: data?.mobile,
      banners: data?.banners,
      website_url: data?.website_url,
    };
    // set categories data
    this.categories = {
      title: data.title,
    };
    // set categories data
    this.aboutData = {
      title: data.title,
      photo_urls: data.photo_urls || [],
      about_description: data.about_description,
      card: data.card,
      catalogs: data.catalogs || [],
      mobile: data?.mobile,
      website_url: data?.website_url,
    };

    // set company video
    this.companyVideo = {
      title: data.title,
      video_urls: data?.video_urls?.[0],
    };
    // set members data
    this.membersData = {
      title: data.title,
      people: data?.people || [],
      ceo_description: data.ceo_description,
      owner: data.owner,
      age: data.age,
      links: data.links || [],
    };

    // set categories data
    this.footerData = {
      title: data?.title,
      description: data?.description,
      country: data.country?.name,
      province: data.province?.name,
      city: data.city?.name,
      address: data?.address,
      mobile:
        data?.mobile?.mobile_number &&
        !data?.mobile?.mobile_number?.includes('000000')
          ? data?.mobile?.prefix + data?.mobile?.mobile_number
          : '-',
      website_url: data?.website_url,
      links: data?.links || [],
    };
    // show cooperation banner
    if (data.title) {
      this.companyTitle = data.title;
    }
    if (data.placing_banner) {
      this.showCooperationBanner = data.placing_banner;
    }

    // set page title base on business title
    if (data.title) {
      this.setPageTitle(data.title[this.currentLang] || data?.title['en']);
    }
  }

  public setPageTitle(content: string) {
    if (content) {
      this._titleService.setTitle(content);
    }
  }

  public setBusinessContactInfo(data: IBusinessContactInfo) {
    // set contact data
    this.contactInfo = [
      {
        title: 'aboutUs.contactData.addressTitle',
        icon: 'map-1',
        description: data?.address || '-',
      },
      {
        title: 'aboutUs.contactData.phoneNumber',
        icon: 'phone',
        description:
          data?.mobile?.mobile_number &&
          !data?.mobile?.mobile_number?.includes('000000')
            ? `${data?.mobile?.prefix} ${data?.mobile?.mobile_number}`
            : '-',
        isNumberMode: true,
      },
      {
        title: 'aboutUs.contactData.whatsAppNumber',
        icon: 'phone',
        description:
          data?.mobile?.mobile_number &&
          !data?.mobile?.mobile_number?.includes('000000')
            ? `${data?.mobile?.prefix} ${data?.mobile?.mobile_number}`
            : '-',
        isNumberMode: true,
      },
      {
        title: 'aboutUs.contactData.fax',
        icon: 'phone',
        description:
          data?.mobile?.mobile_number &&
          !data?.mobile?.mobile_number?.includes('000000')
            ? `${data?.mobile?.prefix} ${data?.mobile?.mobile_number}`
            : '-',
        isNumberMode: true,
      },
      {
        title: 'aboutUs.contactData.email',
        icon: 'mail',
        description: data?.owner?.email || '-',
      },
    ];
  }
}
